import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createBlockedListClient } from './api';
import type {
  BlockedCreativeMutationPayload,
  BlockedCreativesFilter,
  BlockedListFileFormat,
} from './model';

export const blockedListQueryKey = ['supply', 'blocked-list'] as const;
export const blockedListListsQueryKey = [...blockedListQueryKey, 'list'] as const;

function useBlockedListClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createBlockedListClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useBlockedCreativesQuery(filter: BlockedCreativesFilter) {
  const client = useBlockedListClient();
  return useQuery({
    queryKey: [...blockedListListsQueryKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function useBlockedCreativeQuery(id: string | undefined) {
  const client = useBlockedListClient();
  return useQuery({
    queryKey: [...blockedListQueryKey, 'detail', id],
    queryFn: () => client.get(id ?? ''),
    enabled: Boolean(id),
  });
}

export function useBlockedListPublishersQuery(enabled: boolean) {
  const client = useBlockedListClient();
  return useQuery({
    queryKey: [...blockedListQueryKey, 'publishers'],
    queryFn: client.listPublishers,
    enabled,
    staleTime: 300_000,
  });
}

export function useBlockedListMutations() {
  const client = useBlockedListClient();
  const queryClient = useQueryClient();
  const invalidateLists = () =>
    queryClient.invalidateQueries({ queryKey: blockedListListsQueryKey });
  return {
    create: useMutation({ mutationFn: client.create, onSuccess: invalidateLists }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: BlockedCreativeMutationPayload }) =>
        client.update(id, payload),
      onSuccess: invalidateLists,
    }),
    delete: useMutation({ mutationFn: client.delete, onSuccess: invalidateLists }),
    upload: useMutation({
      mutationFn: ({ format, file }: { format: BlockedListFileFormat; file: File }) =>
        client.upload(format, file),
      onSuccess: invalidateLists,
    }),
    download: useMutation({
      mutationFn: ({ format, searchTerm }: { format: BlockedListFileFormat; searchTerm: string }) =>
        client.download(format, searchTerm),
    }),
  };
}
